import {
  buildOptimisticGroupMutationOptions,
  useConfigCatalogMutation,
} from "features/shared/lib/technical-config-tab/api/mutations";
import { useConfigDetailQuery, useConfigGroupsQuery } from "features/shared/lib/technical-config-tab/api/queryHooks";
import { getTechnicalConfigToastError } from "features/shared/lib/technical-config-tab/tableHeaders";
import { useActiveScenarioName } from "features/technical-data/scenario/hooks/useActiveScenarioName";
import i18next from "i18next";
import { useMutation, useQueries, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type { RoomScheduleRealisticCatalog } from "../model/scheduleTypes";
import type {
  RoomConfigCreatePayload,
  RoomConfigSavePayload,
  RoomTypeCatalogItem,
  RoomTypeGroup,
  RoomTypeGroupDetail,
  SaveRoomConfigProps,
} from "../model/types";

import { parseRealisticCatalogResponse } from "../lib/roomScheduleApi";
import { ROOM_SCHEDULE_HEATMAP_KEYS } from "../model/scheduleTypes";
import { ROOM_CONFIG_CACHE_KEYS } from "./cacheKeys";
import { createCatalogItem, deleteRoomConfig, fetchRoomSchedulePreset, saveRoomConfig } from "./callers";
import { invalidateRoomConfigCatalog } from "./invalidateRoomConfigQueries";
import roomConfigTabUrls from "./urls";

export const useGetRoomTypeGroups = (projectId?: string) => {
  const scenario = useActiveScenarioName();
  return useConfigGroupsQuery<RoomTypeGroup>(
    ROOM_CONFIG_CACHE_KEYS.groups(projectId, scenario),
    roomConfigTabUrls.groups(projectId, scenario),
    Boolean(projectId && scenario)
  );
};

export const useGetRoomTypeDetail = (projectId?: string, roomFunctionValue?: string) => {
  const scenario = useActiveScenarioName();
  const enabled = Boolean(projectId && roomFunctionValue && scenario);
  const url = enabled ? roomConfigTabUrls.groupDetail(projectId, roomFunctionValue as string, scenario) : undefined;
  return useConfigDetailQuery<RoomTypeGroupDetail>(
    ROOM_CONFIG_CACHE_KEYS.detail(projectId, roomFunctionValue, scenario),
    url,
    enabled
  );
};

export const useGetRoomTypeCatalog = (enabled = true) =>
  useConfigGroupsQuery<RoomTypeCatalogItem>(ROOM_CONFIG_CACHE_KEYS.catalog, roomConfigTabUrls.catalog(), enabled);

export const useGetRoomSchedulePresets = (enabled: boolean) => {
  const results = useQueries(
    ROOM_SCHEDULE_HEATMAP_KEYS.map((kind) => ({
      enabled,
      queryFn: () => fetchRoomSchedulePreset(kind),
      queryKey: ROOM_CONFIG_CACHE_KEYS.schedulePreset(kind),
      staleTime: Infinity,
    }))
  );

  const catalog: RoomScheduleRealisticCatalog = {};
  ROOM_SCHEDULE_HEATMAP_KEYS.forEach((kind, index) => {
    catalog[kind] = parseRealisticCatalogResponse(results[index]?.data);
  });

  return {
    catalog,
    isLoading: results.some((result) => result.isLoading),
  };
};

export const useSaveRoomConfig = () => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();
  return useMutation(
    (variables: SaveRoomConfigProps) => saveRoomConfig({ ...variables, scenario }),
    buildOptimisticGroupMutationOptions<RoomTypeGroup, SaveRoomConfigProps>({
      applyOptimisticPatch: (group, { data }) => ({
        ...group,
        ...data,
        hasConfig: true,
        ...("saveAsRoomTypeName" in data && data.saveAsRoomTypeName ? { roomTypeName: data.saveAsRoomTypeName } : {}),
      }),
      cacheKeys: ROOM_CONFIG_CACHE_KEYS,
      getProjectId: (variables) => variables.projectId,
      getRecordId: (variables) => variables.roomFunctionValue,
      getScenario: () => scenario,
      matchesGroup: (group, variables) => group.roomFunctionValue === variables.roomFunctionValue,
      queryClient,
      successMessageKey: "TechnicalData.room.saveSuccess",
    })
  );
};

export const useCreateCatalogItem = () =>
  useConfigCatalogMutation({
    invalidateCatalog: invalidateRoomConfigCatalog,
    mutationFn: createCatalogItem,
    successMessageKey: "TechnicalData.room.saveSuccess",
  });

export const useDeleteRoomConfig = () => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();
  return useMutation(
    (variables: { projectId: string; roomFunctionValue: string }) => deleteRoomConfig({ ...variables, scenario }),
    buildOptimisticGroupMutationOptions<RoomTypeGroup, { projectId: string; roomFunctionValue: string }>({
      cacheKeys: ROOM_CONFIG_CACHE_KEYS,
      getProjectId: (variables) => variables.projectId,
      getRecordId: (variables) => variables.roomFunctionValue,
      getScenario: () => scenario,
      queryClient,
      successMessageKey: "TechnicalData.room.deleteSuccess",
    })
  );
};

export type BulkRoomConfigOperation =
  | {
      data: RoomConfigCreatePayload | RoomConfigSavePayload;
      followUpData?: RoomConfigSavePayload;
      hasConfig: boolean;
      kind: "save";
      roomFunctionValue: string;
    }
  | {
      kind: "delete";
      roomFunctionValue: string;
    };

type BulkRoomConfigVariables = {
  onProgress: (completed: number, total: number) => void;
  operations: BulkRoomConfigOperation[];
  projectId: string;
  successMessageKey: string;
};

export const useBulkRoomConfig = () => {
  const scenario = useActiveScenarioName();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ onProgress, operations, projectId }: BulkRoomConfigVariables) => {
      let completed = 0;
      let firstError: unknown;

      await Promise.all(
        operations.map(async (operation) => {
          try {
            if (operation.kind === "delete") {
              await deleteRoomConfig({ projectId, roomFunctionValue: operation.roomFunctionValue, scenario });
            } else if (operation.hasConfig) {
              await saveRoomConfig({
                data: operation.data as RoomConfigSavePayload,
                hasConfig: true,
                projectId,
                roomFunctionValue: operation.roomFunctionValue,
                scenario,
              });
            } else {
              await saveRoomConfig({
                data: operation.data as RoomConfigCreatePayload,
                hasConfig: false,
                projectId,
                roomFunctionValue: operation.roomFunctionValue,
                scenario,
              });
              if (!operation.followUpData) return;

              await saveRoomConfig({
                data: operation.followUpData,
                hasConfig: true,
                projectId,
                roomFunctionValue: operation.roomFunctionValue,
                scenario,
              });
            }
          } catch (error) {
            firstError ??= error;
          } finally {
            completed += 1;
            onProgress(completed, operations.length);
          }
        })
      );

      if (firstError) {
        throw firstError;
      }
    },
    {
      onError: (error: { response?: { data?: unknown } }) => {
        toast.error(getTechnicalConfigToastError(error));
      },
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries(ROOM_CONFIG_CACHE_KEYS.groups(variables.projectId));
        variables.operations.forEach((operation) => {
          queryClient.invalidateQueries(
            ROOM_CONFIG_CACHE_KEYS.detail(variables.projectId, operation.roomFunctionValue)
          );
        });
      },
      onSuccess: (_data, variables) => {
        toast.success(String(i18next.t(variables.successMessageKey)));
      },
    }
  );
};
